import { useCallback, useRef, useState, useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { baselineMeasurementService } from '../../../Domain/BaselineMeasurements/Services';
import type { SearchTasksResponse, Task } from '../../../types/BaselineMeasurements';
import { ComparisonStatusEnum } from '../../../types/BaselineMeasurements';

interface UseTaskPollingOptions {
  comparisonId: string;
  maxAttempts?: number;
  pollingInterval?: number;
  onSuccess?: (data: SearchTasksResponse) => void;
  onTimeout?: () => void;
  onError?: (error: Error) => void;
  onPollingEnd?: () => void;
}

interface UseTaskPollingReturn {
  startPolling: () => void;
  stopPolling: () => void;
  isPolling: boolean;
  tasksData: SearchTasksResponse | undefined;
  attempts: number;
  maxAttempts: number;
  isComplete: boolean;
  isTimeout: boolean;
  isLoading: boolean;
  error: Error | null;
  reset: () => void;
  hasRunningTasks: boolean;
  allTasksCompleted: boolean;
  insurerDataStatus: ComparisonStatusEnum | undefined;
}

const QUERY_KEY = 'searchComparisonTasks';

const hasRunningTasks = (tasks: Task[] | undefined): boolean => {
  return tasks?.length ? tasks.length > 0 : false;
};

const isProcessFinished = (tasks: Task[] | undefined, insurerDataStatus: ComparisonStatusEnum | undefined): boolean => {
  const noRunningTasks = !hasRunningTasks(tasks);
  const statusIsTerminal =
    insurerDataStatus === ComparisonStatusEnum.Completed || insurerDataStatus === ComparisonStatusEnum.Failed;
  return statusIsTerminal && noRunningTasks && tasks !== undefined;
};

export const useTaskPolling = (options: UseTaskPollingOptions): UseTaskPollingReturn => {
  const {
    comparisonId,
    maxAttempts = 10,
    pollingInterval = 2000,
    onSuccess,
    onTimeout,
    onError,
    onPollingEnd,
  } = options;
  const [shouldPoll, setShouldPoll] = useState(false);
  const attemptsRef = useRef(0);
  const hasCalledSuccessRef = useRef(false);
  const hasCalledTimeoutRef = useRef(false);
  const hasCalledPollingEndRef = useRef(false);
  const queryClient = useQueryClient();

  const tasksStatus = useQuery<SearchTasksResponse>({
    queryKey: [QUERY_KEY, comparisonId],
    queryFn: async () => {
      const response = await baselineMeasurementService.searchComparisonTasks(comparisonId);
      return response.data;
    },
    enabled: shouldPoll && !!comparisonId,
    refetchInterval: data => {
      if (!data) {
        return pollingInterval;
      }
      const { tasks, comparison } = data;
      const tasksRunning = hasRunningTasks(tasks);
      const processFinished = isProcessFinished(tasks, comparison?.insurerDataStatus);
      const hasAttemptsLeft = attemptsRef.current < maxAttempts;

      // CASE 1: Process finished (status or no tasks)
      if (processFinished) {
        setShouldPoll(false);
        const wasSuccessful = comparison?.insurerDataStatus === ComparisonStatusEnum.Completed;
        if (wasSuccessful && !hasCalledSuccessRef.current) {
          hasCalledSuccessRef.current = true;
          onSuccess?.(data);
        }

        if (comparison?.insurerDataStatus === ComparisonStatusEnum.Failed) {
          onError?.(new Error('Insurer Data Failed'));
        }

        if (!hasCalledPollingEndRef.current) {
          hasCalledPollingEndRef.current = true;
          onPollingEnd?.();
        }
        return false;
      }
      // CASE 2: There are tasks running
      if (tasksRunning) {
        attemptsRef.current += 1;

        if (hasAttemptsLeft) {
          return pollingInterval;
        } else {
          // CASE 3: Timeout reached and there are tasks running
          setShouldPoll(false);

          if (!hasCalledTimeoutRef.current) {
            hasCalledTimeoutRef.current = true;
            onTimeout?.();
          }
          // Event on timeout, call onPollingEnd to refresh
          if (!hasCalledPollingEndRef.current) {
            hasCalledPollingEndRef.current = true;
            onPollingEnd?.();
          }
          return false;
        }
      }
      // CASE 4: Not running tasks but status in processing
      if (comparison?.insurerDataStatus === ComparisonStatusEnum.Processing && !tasksRunning) {
        attemptsRef.current += 1;

        if (hasAttemptsLeft) {
          return pollingInterval;
        } else {
          setShouldPoll(false);
          if (!hasCalledTimeoutRef.current) {
            hasCalledTimeoutRef.current = true;
            onTimeout?.();
          }

          if (!hasCalledPollingEndRef.current) {
            hasCalledPollingEndRef.current = true;
            onPollingEnd?.();
          }
          return false;
        }
      }
      // Default, stop polling
      return false;
    },
    refetchIntervalInBackground: false,
    retry: false,
    staleTime: 0,
    onError: error => {
      setShouldPoll(false);
      onError?.(error as Error);

      if (!hasCalledPollingEndRef.current) {
        hasCalledPollingEndRef.current = true;
        onPollingEnd?.();
      }
    },
  });
  // Invalidate queries when polling end
  useEffect(() => {
    if (!shouldPoll && (hasCalledSuccessRef.current || hasCalledTimeoutRef.current || hasCalledPollingEndRef.current)) {
      // Delay to ensure the backend is finished processing the request
      const timeoutId = setTimeout(() => {
        // Invalidate queries related to comparison and comparison items
        queryClient.invalidateQueries(['getBaselineMeasurementComparison', comparisonId]);
        queryClient.invalidateQueries(['getBaselineMeasurementComparisonItems', comparisonId]);
      }, 500);
      return () => clearTimeout(timeoutId);
    }
  }, [shouldPoll, comparisonId, queryClient]);

  const startPolling = useCallback(() => {
    setShouldPoll(true);
    attemptsRef.current = 0;
    hasCalledSuccessRef.current = false;
    hasCalledTimeoutRef.current = false;
    hasCalledPollingEndRef.current = false;
  }, []);

  const stopPolling = useCallback(() => {
    setShouldPoll(false);
  }, []);

  const reset = useCallback(() => {
    setShouldPoll(false);
    attemptsRef.current = 0;
    hasCalledSuccessRef.current = false;
    hasCalledTimeoutRef.current = false;
    hasCalledPollingEndRef.current = false;
  }, []);

  const currentHasRunningTasks = hasRunningTasks(tasksStatus.data?.tasks);
  const insurerDataStatus = tasksStatus.data?.comparison?.insurerDataStatus;
  const allTasksCompleted = tasksStatus.data?.tasks?.length === 0;
  const isComplete = insurerDataStatus === ComparisonStatusEnum.Completed && !shouldPoll;
  const isTimeout = attemptsRef.current >= maxAttempts && !shouldPoll && currentHasRunningTasks;

  return {
    startPolling,
    stopPolling,
    isPolling: shouldPoll,
    tasksData: tasksStatus.data,
    attempts: attemptsRef.current,
    maxAttempts,
    isComplete,
    isTimeout,
    isLoading: tasksStatus.isLoading,
    error: tasksStatus.error as Error | null,
    reset,
    hasRunningTasks: currentHasRunningTasks,
    allTasksCompleted,
    insurerDataStatus,
  };
};
